import type { UseFloatingOptions } from '@floating-ui/react';
import {
  autoUpdate,
  shift,
  flip,
  useFloating as useFloatingOriginal,
} from '@floating-ui/react';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import type { StrictOmit } from 'src/utils/utility-types';

export type UseFloatingProps = StrictOmit<
  Partial<UseFloatingOptions>,
  'open' | 'onOpenChange' | 'whileElementsMounted'
> & {
  initialOpen?: boolean;
};

/**
 * This hook is a wrapper around `useFloating` from the floating ui. It simplifies the usage by encapsulating
 * the needed styles for the floating element and providing the open state.
 *
 * See https://floating-ui.com/docs/react.
 */
export function useFloating({
  initialOpen = false,
  ...floatingOptions
}: UseFloatingProps = {}) {
  const [open, setOpen] = useState(initialOpen);

  const floating = useFloatingOriginal({
    open,
    onOpenChange: setOpen,
    middleware: [shift(), flip()],
    whileElementsMounted: (ref, floating, update) =>
      autoUpdate(ref, floating, () => requestAnimationFrame(update)),
    ...floatingOptions,
  });

  return {
    ...floating,
    open,
    setOpen,
    style: {
      left: floating.x,
      top: floating.y,
      position: floating.strategy,
    } as Readonly<CSSProperties>,
  };
}

export type UseFloating = ReturnType<typeof useFloating>;
